import React, { useMemo } from 'react';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import SchemaQuestionCard from '../components/SchemaQuestionCard';
import { useHGS } from '../context/HGSContext';
import { MODULE_SCHEMAS } from '../data/masterData';
import { computeResidualScore, bandFor, FRAUD_TRIGGER_IDS } from '../data/riskUniverse';
import { ArchitectureProgress } from './RiskUniversePage';
import { filterRowsForUser, canFillModuleForms } from '../data/rbac';
import RiskUnpublishedGate from '../components/RiskUnpublishedGate';
import MasterEmptyState from '../components/MasterEmptyState';
import { Plus, ShieldCheck, AlertTriangle, CheckCircle2, Code2, Sparkles, Lock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const ANSWERABLE = ['responseType', 'evidenceFile', 'currentSituation', 'countermeasure', 'targetCompletionDate'];

export default function FraudPage() {
  const { masters, masterMeta, riskUniverse, user, riskPublished } = useHGS();
  const schema = MODULE_SCHEMAS.fraud;
  const meta = masterMeta.fraud;
  const rows = useMemo(() => filterRowsForUser(user, (masters.fraud || []).filter((r) => r.active !== false)), [masters, user]);
  const canEdit = canFillModuleForms(user);
  const masterUploaded = (masters.fraud || []).length > 0;

  // Compute triggering financial risks (R23–R27, R30 at Medium/High)
  const triggeringRisks = useMemo(() => {
    return (riskUniverse || [])
      .filter((r) => FRAUD_TRIGGER_IDS.includes(r.id))
      .map((r) => {
        const { inherent, residual } = computeResidualScore(r);
        return { ...r, inherent, residual, band: bandFor(residual) };
      })
      .filter((r) => r.band === 'Medium' || r.band === 'High');
  }, [riskUniverse]);

  const isTriggered = triggeringRisks.length > 0;

  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.fraudCategory || 'General';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [rows]);

  return (
    <div>
      <TopBar
        title="Fraud Prevention Checklist"
        subtitle="Step 2 of 5 · Triggered by financial risks R23–R27, R30 · control checklist rendered live from master"
        breadcrumbs={['HGS Architecture', 'Fraud Prevention']}
        actions={
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-sm font-semibold rounded-lg shadow-sm transition">
            <Plus className="w-4 h-4" />
            Add Control
          </button>
        }
      />
      <div className="p-8 space-y-5">
        <ArchitectureProgress step={2} />

        {/* If Operating Head hasn't published Risk Universe, block downstream work */}
        {!riskPublished?.published && <RiskUnpublishedGate user={user} />}

        {riskPublished?.published && !canEdit && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="review-mode-banner">
            <Lock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-slate-600 leading-relaxed">
              <span className="font-bold text-slate-900">Review-only mode</span> — Department Heads fill Fraud controls. As a {user?.role?.replace(/_/g, ' ')}, you can see their responses but cannot edit them. Approve / reject from the <span className="font-semibold">Approvals</span> page.
            </div>
          </div>
        )}

        {riskPublished?.published && !masterUploaded && (
          <MasterEmptyState moduleLabel="Fraud Prevention" moduleKey="fraud" accent="#0EA5E9" isAdmin={user?.role === 'admin'} />
        )}

        {riskPublished?.published && masterUploaded && (<>
        {/* Trigger status */}
        {!isTriggered ? (
          <div className="bg-white rounded-xl border border-amber-200 p-6 flex items-start gap-4" data-testid="fraud-gate-not-triggered">
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900">Not yet triggered</h3>
              <p className="text-sm text-slate-600 mt-1">
                The Fraud Prevention Checklist activates automatically when any financial risk (R23–R27, R30) is rated <b>Medium</b> or <b>High</b> in the Risk Universe.
                Currently all financial risks are at Watch/Low level.
              </p>
              <Link to="/risk" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#CC0000] hover:underline">
                Go to Risk Universe → <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-sky-200 p-5" data-testid="fraud-gate-triggered">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Fraud Checklist is active</h3>
                <p className="text-[11px] text-slate-500">Triggered by {triggeringRisks.length} financial risk{triggeringRisks.length > 1 ? 's' : ''} currently rated Medium/High:</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {triggeringRisks.map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 border border-sky-200 text-[11px] font-bold text-sky-900">
                  <span className="font-mono">{r.id}</span>
                  <span className="font-normal">·</span>
                  <span className="font-normal truncate max-w-[240px]">{r.description}</span>
                  <span className={cn('px-1.5 py-0.5 rounded text-[9px] uppercase font-bold', r.band === 'High' ? 'bg-red-600 text-white' : 'bg-amber-400 text-amber-950')}>{r.band}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {isTriggered && (
          <>
        <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-900 to-[#0c2540] text-white">
          <Sparkles className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold flex items-center gap-2 flex-wrap">
              Forms below are rendered live from
              <span className="font-mono px-2 py-0.5 rounded bg-white/10 text-sky-300">{schema.label}</span>
              <span className="font-mono px-2 py-0.5 rounded bg-white/10">{meta?.version || 'v1.0'}</span>
              <StatusBadge status={meta?.status || 'Active'} dot={false} />
            </div>
            <div className="text-[11px] text-slate-300 mt-1">
              {rows.length} active controls · {groups.length} categories · last published by {meta?.lastUploadedBy || '—'} on {meta?.lastUploadedAt || '—'}
            </div>
          </div>
          <Code2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={ShieldCheck} label="Total Controls" value={rows.length} />
          <Stat icon={CheckCircle2} label="Categories" value={groups.length} tone="emerald" />
          <Stat icon={AlertTriangle} label="Open" value="2" tone="amber" />
          <Stat icon={AlertTriangle} label="At Risk" value="1" tone="red" />
        </div>

        {groups.map(([categoryName, categoryRows]) => (
          <div key={categoryName} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-sky-50/40 to-white flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-sky-700" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">{categoryName}</h3>
                <p className="text-[11px] text-slate-500">{categoryRows.length} {categoryRows.length === 1 ? 'control' : 'controls'} · evidence required for Yes responses</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {categoryRows.map((row, i) => (
                <SchemaQuestionCard
                  key={row[schema.idField]}
                  schema={schema}
                  questionRow={row}
                  index={i}
                  questionTextKey="controlQuestion"
                  answerableKeys={ANSWERABLE}
                  accent="#0EA5E9"
                  readOnly={!canEdit}
                />
              ))}
            </div>
          </div>
        ))}
          </>
        )}
        </>)}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    red: 'text-[#CC0000]',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cn('w-4 h-4', tones[tone])} />
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className={cn('text-3xl font-bold', tones[tone])}>{value}</div>
    </div>
  );
}
